// NotificationCenter.jsx - Fixed Version
import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
  Bell, CheckCircle, Package, AlertTriangle, MessageCircle,
  Image, FileText, Phone, Mail, Clock, Star, QrCode, Truck,
  Gift, Filter, Trash2, X, Search, Archive, MoreVertical,
  CheckCheck, Zap,
} from "lucide-react";
import "../../assets/css/receiverdashboard/Notification.css";
import ReceiverMasterLayout from "../../components/layout/ReceiverMasterLayout";
import AppContext from "../../context/AppContext";
import Swal from "sweetalert2";

const ReceiverNotification = () => {
  const { baseUrl, loggedInReceiverId } = useContext(AppContext);

  const [activeTab, setActiveTab]         = useState("all");
  const [searchQuery, setSearchQuery]     = useState("");
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(false);

  // ─────────────────────────────────────────────
  // ✅ Fetch notifications
  // ─────────────────────────────────────────────
  const fetchNotifications = () => {
    if (!loggedInReceiverId) return;
    setLoading(true);
    axios
      .get(`${baseUrl}/receiver/notifications`, {
        params: { receiver_id: loggedInReceiverId },
      })
      .then((response) => {
        if (response.data.success) {
          setNotifications(response.data.notifications);
        } else {
          setNotifications([]);
        }
      })
      .catch((error) => {
        console.log("Notification fetch error:", error);
        setNotifications([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchNotifications();
  }, [loggedInReceiverId]);

  // ─────────────────────────────────────────────
  // ✅ Tabs - categories backend se match
  // ─────────────────────────────────────────────
  const tabs = [
    { id: "all",      label: "All",      count: notifications.length,                                          icon: Bell          },
    { id: "unread",   label: "Unread",   count: notifications.filter((n) => !n.read).length,                  icon: Package       },
    { id: "urgent",   label: "Urgent",   count: notifications.filter((n) => n.category === "urgent").length,  icon: AlertTriangle },
    { id: "donation", label: "Donation", count: notifications.filter((n) => n.category === "donation").length, icon: Gift         },
    { id: "system",   label: "System",   count: notifications.filter((n) => n.category === "system").length,  icon: FileText      },
  ];

  // ─────────────────────────────────────────────
  // Filter logic
  // ─────────────────────────────────────────────
  const getFilteredNotifications = () => {
    let filtered = notifications;

    switch (activeTab) {
      case "unread":   filtered = filtered.filter((n) => !n.read);                         break;
      case "urgent":   filtered = filtered.filter((n) => n.category === "urgent");         break;
      case "donation": filtered = filtered.filter((n) => n.category === "donation");       break;
      case "system":   filtered = filtered.filter((n) => n.category === "system");         break;
      default: break;
    }

    if (searchQuery) {
      filtered = filtered.filter(
        (n) =>
          n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          n.message.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  };

  // ─────────────────────────────────────────────
  // ✅ Mark single as read - endpoint fix
  // ─────────────────────────────────────────────
  const markAsRead = (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );

    axios
      .post(`${baseUrl}/receiver/notifications/${id}/read`, {
        receiver_id: loggedInReceiverId,
      })
      .catch((error) => console.log("Mark read error:", error));
  };

  // ─────────────────────────────────────────────
  // ✅ Mark all as read - endpoint fix
  // ─────────────────────────────────────────────
  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));

    axios
      .post(`${baseUrl}/receiver/notifications/mark-all-read`, {
        receiver_id: loggedInReceiverId,
      })
      .catch((error) => console.log("Mark all read error:", error));
  };

  // ─────────────────────────────────────────────
  // ✅ Delete - DELETE method fix
  // ─────────────────────────────────────────────
  const deleteNotification = (id) => {
    Swal.fire({
      title: "Delete?",
      text: "This notification will be removed!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete!",
    }).then((result) => {
      if (result.isConfirmed) {
        setNotifications((prev) => prev.filter((n) => n.id !== id));

        axios
          .delete(`${baseUrl}/receiver/notifications/${id}`, {
            data: { receiver_id: loggedInReceiverId },
          })
          .then((response) => {
            if (response.data.success) {
              Swal.fire({ title: "Deleted!", text: "Notification removed.", icon: "success" });
            } else {
              fetchNotifications();
            }
          })
          .catch((error) => {
            console.log("Delete error:", error);
            fetchNotifications();
          });
      }
    });
  };

  // Archive = UI only
  const archiveNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const handleAction = (notification, action) => {
    markAsRead(notification.id);
  };

  // ─────────────────────────────────────────────
  // Icon & Color mapper - category se match
  // ─────────────────────────────────────────────
  const getIcon = (category) => {
    const iconMap = {
      donation: Gift,
      urgent:   AlertTriangle,
      system:   FileText,
      badge:    Star,
    };
    return iconMap[category] || Bell;
  };

  const getColor = (category) => {
    const colorMap = {
      donation: "#52C41A",
      urgent:   "#FF6B4A",
      system:   "#1F7A7A",
      badge:    "#FFB800",
    };
    return colorMap[category] || "#1F7A7A";
  };

  // ✅ created_at ko readable format mein
  const formatTime = (created_at) => {
    if (!created_at) return "Just now";
    return new Date(created_at).toLocaleDateString(undefined, {
      day: "numeric", month: "short", year: "numeric",
    });
  };

  const filteredNotifications = getFilteredNotifications();
  const unreadCount = notifications.filter((n) => !n.read).length;
  const urgentCount = notifications.filter((n) => n.category === "urgent" && !n.read).length;

  return (
    <ReceiverMasterLayout>
      <div className="nc-wrapper">

        {/* ── Stats Summary ── */}
        <div className="nc-stats-bar">
          <div className="nc-stat-item nc-stat-total">
            <div className="nc-stat-icon-wrapper"><Bell size={20} /></div>
            <div className="nc-stat-content">
              <h3 className="nc-stat-number">{notifications.length}</h3>
              <p className="nc-stat-label">Total</p>
            </div>
          </div>

          <div className="nc-stat-item nc-stat-unread">
            <div className="nc-stat-icon-wrapper"><Package size={20} /></div>
            <div className="nc-stat-content">
              <h3 className="nc-stat-number">{unreadCount}</h3>
              <p className="nc-stat-label">Unread</p>
            </div>
            {unreadCount > 0 && <div className="nc-stat-pulse"></div>}
          </div>

          <div className="nc-stat-item nc-stat-urgent">
            <div className="nc-stat-icon-wrapper"><Zap size={20} /></div>
            <div className="nc-stat-content">
              <h3 className="nc-stat-number">{urgentCount}</h3>
              <p className="nc-stat-label">Urgent</p>
            </div>
            {urgentCount > 0 && <div className="nc-stat-pulse urgent"></div>}
          </div>
        </div>

        {/* ── Filter Tabs ── */}
        <div className="nc-filter-tabs">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`nc-filter-tab ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {TabIcon && <TabIcon size={18} />}
                <span>{tab.label}</span>
                {tab.count > 0 && (
                  <span className="nc-filter-badge">{tab.count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── Header Section ── */}
        <div className="nc-header-section">
          <div className="nc-header-info">
            <h2 className="nc-page-title">
              {tabs.find((t) => t.id === activeTab)?.label || "All"} Notifications
            </h2>
            <p className="nc-page-subtitle">
              {filteredNotifications.length} notification
              {filteredNotifications.length !== 1 ? "s" : ""}
              {unreadCount > 0 && ` · ${unreadCount} unread`}
            </p>
          </div>
          <div className="nc-header-actions">
            <button className="nc-action-button" onClick={markAllAsRead} title="Mark all as read">
              <CheckCheck size={18} />
              <span>Mark All Read</span>
            </button>
            <button className="nc-icon-button" title="More options">
              <MoreVertical size={18} />
            </button>
          </div>
        </div>

        {/* ── Search Bar ── */}
        <div className="nc-search-container">
          <div className="nc-search-icon"><Search size={18} /></div>
          <input
            type="text"
            className="nc-search-field"
            placeholder="Search notifications by title or message..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="nc-search-clear-btn" onClick={() => setSearchQuery("")}>
              <X size={16} />
            </button>
          )}
        </div>

        {/* ── Notifications List ── */}
        <div className="nc-notifications-container">
          {loading ? (
            <div className="nc-empty-state">
              <div className="nc-empty-icon"><Bell size={64} /></div>
              <h3 className="nc-empty-title">Loading Notifications...</h3>
              <p className="nc-empty-text">Please wait while we fetch your updates.</p>
            </div>

          ) : filteredNotifications.length === 0 ? (
            <div className="nc-empty-state">
              <div className="nc-empty-icon"><Bell size={64} /></div>
              <h3 className="nc-empty-title">No Notifications Found</h3>
              <p className="nc-empty-text">
                {searchQuery
                  ? `No results found for "${searchQuery}"`
                  : "You're all caught up! Check back later for updates."}
              </p>
            </div>

          ) : (
            <div className="nc-notifications-grid">
              {filteredNotifications.map((notification) => {
                const Icon  = getIcon(notification.category);  // ✅ category se
                const color = getColor(notification.category); // ✅ category se

                return (
                  <div
                    key={notification.id}
                    className={`nc-notification-card ${!notification.read ? "unread" : ""} priority-${notification.category}`}
                    onClick={() => markAsRead(notification.id)}
                  >
                    {/* Left Border */}
                    <div className="nc-card-border" style={{ background: color }}></div>

                    {/* Icon */}
                    <div className="nc-card-icon-wrapper">
                      <div
                        className="nc-card-icon"
                        style={{ background: `${color}20`, borderColor: `${color}40` }}
                      >
                        <Icon size={20} style={{ color }} />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="nc-card-content">
                      <div className="nc-card-header">
                        <div className="nc-card-title-group">
                          <h4 className="nc-card-title">{notification.title}</h4>
                          {notification.category === "urgent" && (
                            <span className="nc-priority-badge">URGENT</span>
                          )}
                        </div>
                        <div className="nc-card-menu">
                          <button
                            className="nc-menu-btn"
                            onClick={(e) => { e.stopPropagation(); archiveNotification(notification.id); }}
                            title="Archive"
                          >
                            <Archive size={14} />
                          </button>
                          <button
                            className="nc-menu-btn delete"
                            onClick={(e) => { e.stopPropagation(); deleteNotification(notification.id); }}
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>

                      <p className="nc-card-message">{notification.message}</p>

                      {/* ✅ donor info show karo agar ho */}
                      {notification.donor_id && (
                        <small style={{ color: "#888", display: "block", marginBottom: "6px" }}>
                          👤 From Donor #{notification.donor_id}
                        </small>
                      )}

                      {/* ✅ donation id show karo agar ho */}
                      {notification.donation_id && (
                        <small style={{ color: "#888", display: "block", marginBottom: "6px" }}>
                          📦 Donation #{notification.donation_id}
                        </small>
                      )}

                      <div className="nc-card-footer">
                        <div className="nc-card-time">
                          <Clock size={13} />
                          {/* ✅ created_at use karo timestamp ki jagah */}
                          <span>{formatTime(notification.created_at)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Unread Indicator */}
                    {!notification.read && (
                      <div className="nc-unread-indicator" style={{ background: color }}></div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Footer Info ── */}
        {filteredNotifications.length > 0 && (
          <div className="nc-footer-info">
            <div className="nc-footer-items">
              <div className="nc-footer-item">
                <div className="nc-footer-icon"><Phone size={14} /></div>
                <span>SMS Alerts Active</span>
              </div>
              <div className="nc-footer-item">
                <div className="nc-footer-icon"><Mail size={14} /></div>
                <span>Email Enabled</span>
              </div>
            </div>
            <button className="nc-settings-btn">
              <Filter size={14} />
              Manage Preferences
            </button>
          </div>
        )}

      </div>
    </ReceiverMasterLayout>
  );
};

export default ReceiverNotification;